// src/components/game/RiddleGame.jsx
// Logic riddle puzzle — scalable across all 25 riddle levels.
//
// Difficulty comes entirely from config.difficulty:
//   characterCount   → how many characters are in this riddle (5–8)
//   clueCount        → how many clues are shown
//   redHerrings      → how many decoy clues are appended
//   timeLimitSeconds → optional countdown (null = no timer)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSound } from '../../hooks/useSound';
import { getRiddleScenario, generateNumberMap, shuffle } from '../../utils/riddleData';

const RIDDLE_MESSAGES = [
  'Drag a character to start!',
  'Nice thinking! 🧠',
  'Read the clues carefully!',
  "You're getting warmer! 🔥",
  'Almost cracked it!',
  'Great detective work!',
  'Think it through!',
  "You've got this!",
  'So close!',
  'Use the clues wisely!',
];

const ordinalLabel = (i) =>
  ['1ST', '2ND', '3RD', '4TH', '5TH', '6TH', '7TH', '8TH'][i] || `${i + 1}TH`;

/**
 * @param {object}   props
 * @param {object}   props.config    — full level config from getLevelConfig()
 * @param {Function} props.onWin     — (stars: number) => void
 * @param {boolean}  props.gameOver  — true once win/lose has fired
 */
const RiddleGame = ({ config, onWin, gameOver }) => {
  const { playHover, playClick, playDrop, playError } = useSound();
  const { characterCount, clueCount, redHerrings, timeLimitSeconds } = config.difficulty;

  // ── Stable scenario generated once per mount ──────────────────────────────
  const scenarioRef = useRef(null);
  const numberMapRef = useRef(null);

  if (!scenarioRef.current) {
    scenarioRef.current = getRiddleScenario(characterCount);
  }
  const scenario = scenarioRef.current;

  if (!numberMapRef.current) {
    numberMapRef.current = generateNumberMap(scenario.characters.map(c => c.id));
  }

  // ── State ─────────────────────────────────────────────────────────────────
  const [numberMap,     setNumberMap]     = useState(() => ({ ...numberMapRef.current }));
  const [slots,         setSlots]         = useState(() => new Array(scenario.characters.length).fill(null));
  const [bank,          setBank]          = useState(() => shuffle(scenario.characters.map(c => c.id)));
  const [dragItem,      setDragItem]      = useState(null);
  const [checkedClues,  setCheckedClues]  = useState([]);
  const [codeInput,     setCodeInput]     = useState('');
  const [codeError,     setCodeError]     = useState(false);
  const [codeShake,     setCodeShake]     = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [msgIndex,      setMsgIndex]      = useState(0);
  const [timeLeft,      setTimeLeft]      = useState(timeLimitSeconds);

  // ── Build displayed clues (real + red herrings) ───────────────────────────
  const realClues = scenario.clues.slice(0, Math.min(clueCount, scenario.clues.length));
  const herringPool = scenario.redHerringClues || [];
  const herringClues = herringPool.slice(0, redHerrings);
  const displayedClues = shuffle([...realClues, ...herringClues]);

  // ── Correct answer ────────────────────────────────────────────────────────
  const correctCode = scenario.correctOrder.map(id => String(numberMap[id])).join('');

  // ── Optional countdown timer ──────────────────────────────────────────────
  useEffect(() => {
    if (!timeLimitSeconds || gameOver) return;
    if (timeLeft === null) return;
    if (timeLeft <= 0) {
      // Time's up — no automatic lose here; parent handles it via onLose if wired
      return;
    }
    const interval = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timeLimitSeconds, gameOver, timeLeft]);

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const onDragStart = (e, id, from, idx) => {
    setDragItem({ id, from, idx });
    e.dataTransfer.effectAllowed = 'move';
    playClick();
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const dropOnSlot = (e, slotIdx) => {
    e.preventDefault();
    if (!dragItem) return;

    const newSlots = [...slots];
    const newBank  = [...bank];
    const existing = newSlots[slotIdx];

    if (dragItem.from === 'bank') {
      if (existing) newBank.push(existing);
      const bankIdx = newBank.indexOf(dragItem.id);
      if (bankIdx !== -1) newBank.splice(bankIdx, 1);
      newSlots[slotIdx] = dragItem.id;
    } else {
      newSlots[dragItem.idx] = existing || null;
      newSlots[slotIdx] = dragItem.id;
    }

    setSlots(newSlots);
    setBank(newBank.filter(Boolean));
    setDragItem(null);
    playDrop();
    setMsgIndex(i => (i + 1) % RIDDLE_MESSAGES.length || 1);
  };

  const dropOnBank = (e) => {
    e.preventDefault();
    if (!dragItem || dragItem.from === 'bank') { setDragItem(null); return; }
    const newSlots = [...slots];
    const newBank  = [...bank];
    newSlots[dragItem.idx] = null;
    newBank.push(dragItem.id);
    setSlots(newSlots);
    setBank(newBank);
    setDragItem(null);
    playClick();
  };

  const toggleClue = (i) => {
    setCheckedClues(prev =>
      prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]
    );
  };

  // ── Code entry ────────────────────────────────────────────────────────────
  const handleEnterCode = useCallback(() => {
    if (gameOver) return;
    playClick();

    const derivedCode = slots.map(id => (id ? String(numberMap[id]) : '_')).join('');
    const isCorrect   = codeInput.trim() === correctCode || derivedCode === correctCode;

    if (isCorrect) {
      const earnedStars = wrongAttempts === 0 ? 3 : wrongAttempts <= 2 ? 2 : 1;
      onWin(earnedStars);
    } else {
      setWrongAttempts(a => a + 1);
      setCodeError(true);
      setCodeShake(true);
      playError();
      setTimeout(() => setCodeShake(false), 600);
      setTimeout(() => setCodeError(false), 2000);
    }
  }, [gameOver, playClick, slots, numberMap, codeInput, correctCode, wrongAttempts, onWin, playError]);

  // ── Reset ─────────────────────────────────────────────────────────────────
  const handleReset = () => {
    if (gameOver) return;
    playClick();
    // Regenerate number map only; scenario stays stable
    const freshMap = generateNumberMap(scenario.characters.map(c => c.id));
    numberMapRef.current = freshMap;
    setNumberMap(freshMap);
    setBank(shuffle(scenario.characters.map(c => c.id)));
    setSlots(new Array(scenario.characters.length).fill(null));
    setCodeInput('');
    setCodeError(false);
    setCheckedClues([]);
    setMsgIndex(0);
    setWrongAttempts(0);
    setTimeLeft(timeLimitSeconds);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const codeLength = scenario.characters.length;

  return (
    <div className="lvl2-body" style={{ gridColumn: '1 / -1' }}>

      {/* ── Character sidebar ── */}
      <div className="lvl2-left">
        <div className="lvl2-char-wrap">
          <div className="lvl2-glowzy" />
          <div className="lvl2-char-bubble">
            <div className="lvl2-char-name">GLOWZY</div>
            <div className="lvl2-char-msg">{RIDDLE_MESSAGES[msgIndex]}</div>
          </div>
        </div>

        {/* Timer (if enabled) */}
        {timeLimitSeconds && (
          <div
            className="lvl2-timer"
            style={{ color: timeLeft < 30 ? 'var(--pink)' : 'var(--green)', marginTop: 12, textAlign: 'center', fontFamily: 'orb', fontSize: '1.2rem' }}
          >
            {timeLeft !== null ? `${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, '0')}` : '--:--'}
          </div>
        )}

        <button className="lvl2-reset-btn" onClick={handleReset} onMouseEnter={playHover} style={{ marginTop: 16 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
          </svg>
          RESET
        </button>
      </div>

      {/* ── Puzzle area ── */}
      <div className="lvl2-center">

        {/* Clue box */}
        <div className="lvl2-clue-box">
          <p className="lvl2-clue-intro">{scenario.intro}</p>
          <ul className="lvl2-clue-list">
            {displayedClues.map((clue, i) => (
              <li key={i} className="lvl2-clue-item">
                <button
                  className={`lvl2-checkbox ${checkedClues.includes(i) ? 'checked' : ''}`}
                  onClick={() => toggleClue(i)}
                  onMouseEnter={playHover}
                >
                  {checkedClues.includes(i) && <span className="lvl2-check">✓</span>}
                </button>
                <span className={checkedClues.includes(i) ? 'lvl2-clue-done' : ''}>
                  {clue}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Character bank */}
        <div className="lvl2-bank" onDragOver={onDragOver} onDrop={dropOnBank}>
          {bank.map(id => {
            const c = scenario.characters.find(x => x.id === id);
            if (!c) return null;
            return (
              <div
                key={id}
                className="lvl2-bank-char"
                draggable
                onDragStart={e => onDragStart(e, id, 'bank', null)}
                onMouseEnter={playHover}
                style={{ borderColor: `${c.color}88`, boxShadow: `0 0 12px ${c.color}44` }}
              >
                <span className="lvl2-char-number-badge">{numberMap[id]}</span>
                <span className="lvl2-bank-emoji">{c.emoji}</span>
              </div>
            );
          })}
          {bank.length === 0 && (
            <p className="lvl2-bank-empty">All characters placed!</p>
          )}
        </div>

        {/* Drop slots */}
        <div className="lvl2-slots-row">
          {slots.map((id, i) => {
            const c = id ? scenario.characters.find(x => x.id === id) : null;
            return (
              <div
                key={i}
                className={`lvl2-slot ${id ? 'filled' : ''}`}
                onDragOver={onDragOver}
                onDrop={e => dropOnSlot(e, i)}
              >
                {id && c ? (
                  <div
                    className="lvl2-slot-char"
                    draggable
                    onDragStart={e => onDragStart(e, id, 'slot', i)}
                    onMouseEnter={playHover}
                    style={{
                      background:  `radial-gradient(circle at 35% 35%, ${c.color}44, ${c.color}11)`,
                      boxShadow:   `0 0 20px ${c.color}66`,
                      borderColor: `${c.color}88`,
                    }}
                  >
                    <span className="lvl2-char-number-badge">{numberMap[id]}</span>
                    <span className="lvl2-slot-emoji">{c.emoji}</span>
                  </div>
                ) : (
                  <span className="lvl2-slot-placeholder">?</span>
                )}
                <span className="lvl2-slot-label">{ordinalLabel(i)}</span>
              </div>
            );
          })}
        </div>

        {/* Code entry */}
        <div className="lvl2-code-row">
          <input
            className={`lvl2-code-input ${codeShake ? 'shake' : ''} ${codeError ? 'error' : ''}`}
            type="text"
            maxLength={codeLength}
            placeholder="ENTER CODE"
            value={codeInput}
            onChange={e => setCodeInput(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={e => e.key === 'Enter' && handleEnterCode()}
            spellCheck={false}
            inputMode="numeric"
          />
          <button className="lvl2-enter-btn" onClick={handleEnterCode} onMouseEnter={playHover}>
            ENTER CODE
          </button>
        </div>

        {codeError && (
          <p className="lvl2-error-msg">Wrong order! Try again. 💡</p>
        )}

      </div>
    </div>
  );
};

export default RiddleGame;